import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useToast } from '../../components/Toast/ToastProvider';

const EMPRESA_DEFAULT = process.env.REACT_APP_EMPRESA_DEFAULT || 'CIMSA';

const AsistenteReclamos = ({ empresaActual = EMPRESA_DEFAULT }) => {

    const { showToast } = useToast();

    // --- DATOS ---
    const [data, setData]       = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    // --- FILTROS ---
    const [searchTerm,      setSearchTerm]      = useState('');
    const [searchProveedor, setSearchProveedor] = useState('');
    const [filterEstado,    setFilterEstado]    = useState('TODOS');

    // --- TABLA ---
    const [expandedRows, setExpandedRows] = useState([]);
    const [selectedDocs, setSelectedDocs] = useState([]);

    // --- MODAL PREVIEW CORREO ---
    const [showPreview,  setShowPreview]  = useState(false);
    const [mailSubject,  setMailSubject]  = useState('');
    const [mailBody,     setMailBody]     = useState('');
    const [mailTo,       setMailTo]       = useState('');       // email proveedor (editable)
    const [mailToManual, setMailToManual] = useState(false);    // indica si vino vacío de SAP

    // --- MODAL CREDENCIALES ---
    const [showCredModal, setShowCredModal] = useState(false);
    const [credError,     setCredError]     = useState('');
    const [sending,       setSending]       = useState(false);

    // ── CARGA DE DATOS ────────────────────────────────────────
    const cargarDatos = useCallback(async (signal) => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get('/compras/seguimiento-oc', {
                params: { empresa: empresaActual },
                signal
            });
            setData(res.data);
        } catch (err) {
            // Ignorar errores provocados por la cancelación del request al desmontar
            if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;
            setError(err.response?.data?.error || err.message);
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, [empresaActual]);

    useEffect(() => {
        const controller = new AbortController();
        cargarDatos(controller.signal);
        return () => controller.abort();
    }, [cargarDatos]);

    // ── LÓGICA DE FECHAS ──────────────────────────────────────
    const calcularEstadoFecha = (fechaStr) => {
        if (!fechaStr) return { code: 'OK', label: 'Sin fecha', color: 'bg-secondary', icon: '', text: 'text-white', days: 999 };
        const hoy     = new Date(); hoy.setHours(0,0,0,0);
        const entrega = new Date(fechaStr);
        const diffDays = Math.ceil((entrega - hoy) / (1000 * 60 * 60 * 24));
        if (diffDays < 0)  return { code: 'VENCIDO', label: 'Vencido',  color: 'bg-danger',  text: 'text-white', days: diffDays };
        if (diffDays <= 3) return { code: 'PROXIMO', label: 'Activar',  color: 'bg-warning', text: 'text-dark',  days: diffDays };
        return              { code: 'OK',      label: 'En fecha', color: 'bg-success', text: 'text-white', days: diffDays };
    };

    // ── FILTRADO ──────────────────────────────────────────────
    const filteredData = data.filter(row => {
        const term = searchTerm.toLowerCase();
        const matchGeneral = !term ||
            row.docNum.toString().includes(term) ||
            row.comprador.toLowerCase().includes(term) ||
            row.items.some(i =>
                i.itemCode.toLowerCase().includes(term) ||
                i.dscription.toLowerCase().includes(term)
            );
        const matchProv  = !searchProveedor || row.proveedor.toLowerCase().includes(searchProveedor.toLowerCase());
        const estado     = calcularEstadoFecha(row.fechaEntrega);
        const matchEstado = filterEstado === 'TODOS' || estado.code === filterEstado;
        return matchGeneral && matchProv && matchEstado;
    });

    // ── HANDLERS TABLA ────────────────────────────────────────
    const toggleRow      = id => setExpandedRows(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    const handleCheckbox = id => setSelectedDocs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    // ── ARMAR CORREO ──────────────────────────────────────────
    const abrirPreviewCorreo = (esMasivo = false, rowIndividual = null) => {
        let seleccionados = [];

        if (esMasivo) {
            seleccionados = data.filter(d => selectedDocs.includes(d.docEntry));
            if (seleccionados.length === 0) return;
            const primerProv = seleccionados[0].cardCode;
            if (!seleccionados.every(d => d.cardCode === primerProv)) {
                showToast('Seleccioná órdenes de un ÚNICO proveedor para envío masivo.', 'warning');
                return;
            }
        } else {
            seleccionados = [rowIndividual];
        }

        const hayVencidos  = seleccionados.some(s => calcularEstadoFecha(s.fechaEntrega).days < 0);
        const tipoAccion   = hayVencidos ? 'RECLAMO' : 'SEGUIMIENTO';
        const emailDestino = seleccionados[0].emailProveedor || '';
        const sinEmail     = !emailDestino;

        let body = `Estimado Proveedor,\nA quien corresponda:\n\n`;
        body += hayVencidos
            ? `Nos ponemos en contacto desde el Departamento de Compras para solicitarles fecha de entrega de las siguientes Órdenes de Compra, ya que el plazo de entrega se encuentra vencido:\n\n`
            : `Nos ponemos en contacto desde el Departamento de Compras para consultar la fecha de entrega de las siguientes Órdenes de Compra pendientes:\n\n`;

        seleccionados.forEach(oc => {
            const fecha = oc.fechaEntrega ? oc.fechaEntrega.split('-').reverse().join('/') : 'sin fecha';
            body += `• OC N° ${oc.docNum} - Fecha de entrega pactada: ${fecha}\n`;
            oc.items.forEach(item => {
                if (item.cantPend > 0) body += `    - ${item.dscription} (${item.cantPend} un.)\n`;
            });
            body += '\n';
        });

        body += `Necesitamos que confirmen por este medio la nueva fecha de entrega.\n\nSaludos cordiales,\n\nDepartamento de Compras\nGrupo Codylsa`;

        setMailSubject(`${tipoAccion} ENTREGA OC PENDIENTES - GRUPO CODYLSA`);
        setMailBody(body);
        setMailTo(emailDestino);
        setMailToManual(sinEmail);   // activa campo editable si vino vacío
        setShowPreview(true);
    };

    // ── ABRIR MODAL CREDENCIALES (precarga datos del usuario logueado) ──
    const confirmarEnvio = () => {
        if (!mailTo || !mailTo.includes('@')) {
            showToast('Ingresá un email de destino válido para el proveedor.', 'warning');
            return;
        }
        // Ya no se piden credenciales: el correo sale por la casilla de Compras
        // que configura el Centro de Notificaciones.
        setCredError('');
        setShowPreview(false);
        setShowCredModal(true);
    };

    // ── ENVÍO REAL ────────────────────────────────────────────
    // Sale por el Centro de Notificaciones, con la casilla de Compras. Antes
    // cada persona tipeaba acá su clave de aplicacion de Gmail y viajaba al
    // backend como credencial SMTP.
    const handleEnviarConCredenciales = async () => {
        setSending(true);
        setCredError('');
        try {
            const primera = data.find(d => selectedDocs.includes(d.docEntry)) || {};
            const { data: r } = await api.post('/compras/seguimiento-oc/enviar-reclamo', {
                emailTo:   mailTo,
                subject:   mailSubject,
                body:      mailBody,
                docNum:    primera.docNum,
                docEntry:  primera.docEntry,
                proveedor: primera.cardName,
                empresa:   process.env.REACT_APP_EMPRESA_DEFAULT || 'CIMSA',
                // Respaldo por si el token no trae el mail del usuario: la OC
                // ya viene con el del vendedor asignado.
                emailComprador: primera.emailComprador || ''
            });
            // El backend responde 200 con ok:false cuando el envio se rechaza:
            // es el resultado, no una falla del sistema.
            if (!r.ok) { setCredError(r.error || 'No se pudo enviar.'); return; }
            setShowCredModal(false);
            showToast(
                r.desviado
                    ? `Enviado a la casilla de pruebas (el destinatario real era ${mailTo}).`
                    : `Correo enviado exitosamente a ${mailTo}`,
                r.desviado ? 'warning' : 'success', r.desviado ? 9000 : 5000);
            setSelectedDocs([]);
        } catch (err) {
            setCredError(err.response?.data?.error || 'Error al enviar.');
        } finally {
            setSending(false);
        }
    };

    // ── RENDER ────────────────────────────────────────────────
    if (loading) return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
            <div className="spinner-border text-primary me-3" role="status"></div>
            <span className="text-muted">Cargando órdenes de compra...</span>
        </div>
    );

    if (error) return (
        <div className="alert alert-danger m-4">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Error al cargar datos: {error}
            <button className="btn btn-sm btn-outline-danger ms-3" onClick={cargarDatos}>Reintentar</button>
        </div>
    );

    return (
        <div className="container-fluid mt-4">

            {/* BARRA DE FILTROS */}
            <div className="card shadow-sm mb-3">
                <div className="card-body py-3">
                    <div className="row g-3 align-items-center">
                        <div className="col-md-3">
                            <h5 className="mb-0 text-primary fw-bold">
                                <i className="bi bi-grid-3x3-gap-fill me-2"></i>Seguimiento OC
                            </h5>
                        </div>
                        <div className="col-md-3">
                            <div className="input-group">
                                <span className="input-group-text bg-light"><i className="bi bi-truck"></i></span>
                                <input type="text" className="form-control" placeholder="Filtrar Proveedor..."
                                    value={searchProveedor} onChange={e => setSearchProveedor(e.target.value)} />
                            </div>
                        </div>
                        <div className="col-md-3 text-center">
                            <div className="btn-group w-100" role="group">
                                <button type="button" className={`btn btn-sm ${filterEstado === 'TODOS'   ? 'btn-secondary'      : 'btn-outline-secondary'}`} onClick={() => setFilterEstado('TODOS')}>Todo</button>
                                <button type="button" className={`btn btn-sm ${filterEstado === 'VENCIDO' ? 'btn-danger'         : 'btn-outline-danger'}`}   onClick={() => setFilterEstado('VENCIDO')}>Vencidos</button>
                                <button type="button" className={`btn btn-sm ${filterEstado === 'PROXIMO' ? 'btn-warning'        : 'btn-outline-warning'}`}  onClick={() => setFilterEstado('PROXIMO')}>Próximos</button>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="input-group">
                                <span className="input-group-text bg-light"><i className="bi bi-search"></i></span>
                                <input type="text" className="form-control" placeholder="Buscar OC, Item..."
                                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ACCIÓN MASIVA */}
            {selectedDocs.length > 0 && (
                <div className="alert alert-info d-flex justify-content-between align-items-center shadow-sm py-2">
                    <span><i className="bi bi-check-circle-fill me-2"></i><strong>{selectedDocs.length}</strong> órdenes seleccionadas.</span>
                    <button className="btn btn-primary btn-sm" onClick={() => abrirPreviewCorreo(true)}>
                        <i className="bi bi-envelope-paper-fill me-2"></i>Redactar Correo Masivo
                    </button>
                </div>
            )}

            {/* TABLA */}
            <div className="card shadow-sm border-0">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0 align-middle">
                            <thead className="table-dark">
                                <tr>
                                    <th style={{ width: 40 }} className="text-center"><i className="bi bi-check2-square"></i></th>
                                    <th style={{ width: 40 }}></th>
                                    <th className="text-center" style={{ width: 130 }}>Status</th>
                                    <th>N° OC</th>
                                    <th>Proveedor</th>
                                    <th>Comprador</th>
                                    <th>Solicitante</th>
                                    <th className="text-center" style={{ width: 100 }}>Avance</th>
                                    <th className="text-center" style={{ width: 80 }}>Acción</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.length > 0 ? filteredData.map(row => {
                                    const isExpanded = expandedRows.includes(row.docEntry);
                                    const isSelected = selectedDocs.includes(row.docEntry);
                                    const status     = calcularEstadoFecha(row.fechaEntrega);
                                    const totalSol   = row.items.reduce((a, i) => a + i.cantSol,  0);
                                    const totalPend  = row.items.reduce((a, i) => a + i.cantPend, 0);
                                    const pct        = totalSol > 0 ? Math.round(((totalSol - totalPend) / totalSol) * 100) : 0;
                                    const sinEmail   = !row.emailProveedor;

                                    return (
                                        <React.Fragment key={row.docEntry}>
                                            <tr style={{ backgroundColor: isSelected ? '#e8f0fe' : (isExpanded ? '#f8f9fa' : 'white') }}>
                                                <td className="text-center">
                                                    <input type="checkbox" className="form-check-input" checked={isSelected}
                                                        onChange={() => handleCheckbox(row.docEntry)}
                                                        style={{ cursor: 'pointer', transform: 'scale(1.2)' }} />
                                                </td>
                                                <td className="text-center text-primary" onClick={() => toggleRow(row.docEntry)} style={{ cursor: 'pointer' }}>
                                                    <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                                                </td>
                                                <td className="text-center" onClick={() => toggleRow(row.docEntry)} style={{ cursor: 'pointer' }}>
                                                    <span className={`badge ${status.color} ${status.text} w-100 shadow-sm`}>
                                                        {status.days < 0 ? 'Vencido' : `${status.days} días`}
                                                    </span>
                                                    <div style={{ fontSize: '0.7rem' }} className="text-muted mt-1">
                                                        {row.fechaEntrega?.split('-').reverse().join('/') || '—'}
                                                    </div>
                                                </td>
                                                <td className="fw-bold">{row.docNum}</td>
                                                <td className="small">
                                                    {row.proveedor}
                                                    {/* Indicador visual si el proveedor no tiene email */}
                                                    {sinEmail && (
                                                        <span className="badge bg-warning text-dark ms-2 small" title="Sin email en SAP">
                                                            <i className="bi bi-envelope-x me-1"></i>Sin email
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="small">{row.comprador}</td>
                                                <td className="small text-muted">{row.solicitante || '—'}</td>
                                                <td className="text-center">
                                                    <div className="progress" style={{ height: 6 }}>
                                                        <div className="progress-bar bg-primary" style={{ width: `${pct}%` }}></div>
                                                    </div>
                                                    <small className="text-muted">{pct}%</small>
                                                </td>
                                                <td className="text-center">
                                                    {totalPend > 0 && (
                                                        <button className="btn btn-outline-secondary btn-sm border-0"
                                                            onClick={() => abrirPreviewCorreo(false, row)}
                                                            title="Redactar Reclamo">
                                                            <i className="bi bi-pencil-square fs-5"></i>
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>

                                            {isExpanded && (
                                                <tr>
                                                    <td colSpan="9" className="p-0 bg-light border-bottom border-secondary">
                                                        <div className="p-3 ps-5">
                                                            <table className="table table-sm table-bordered bg-white small mb-0 shadow-sm">
                                                                <thead className="table-secondary">
                                                                    <tr>
                                                                        <th>Cód</th>
                                                                        <th>Desc</th>
                                                                        <th>Art. Original (SolPed)</th>
                                                                        <th className="text-center">Sol.</th>
                                                                        <th className="text-center">Pend.</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {row.items.map((item, idx) => (
                                                                        <tr key={idx}>
                                                                            <td className="fw-bold">{item.itemCode}</td>
                                                                            <td>{item.dscription}</td>
                                                                            <td className="text-muted fst-italic">
                                                                                {item.itemOriginal !== item.itemCode
                                                                                    ? <span className="text-danger">{item.itemOriginal} <i className="bi bi-arrow-right"></i> {item.itemCode}</span>
                                                                                    : <span className="text-muted">—</span>
                                                                                }
                                                                            </td>
                                                                            <td className="text-center">{item.cantSol}</td>
                                                                            <td className="text-center text-danger fw-bold">{item.cantPend}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                }) : (
                                    <tr><td colSpan="9" className="text-center py-5 text-muted">No se encontraron resultados.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* ── MODAL 1: PREVIEW CORREO ────────────────────────────── */}
            {showPreview && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal fade show d-block" tabIndex="-1">
                        <div className="modal-dialog modal-lg modal-dialog-centered">
                            <div className="modal-content shadow-lg border-0">
                                <div className="modal-header bg-primary text-white">
                                    <h5 className="modal-title">
                                        <i className="bi bi-envelope-check me-2"></i>Vista Previa del Correo
                                    </h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowPreview(false)}></button>
                                </div>
                                <div className="modal-body bg-light">

                                    {/* EMAIL DESTINATARIO — editable siempre, resaltado si vino vacío */}
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">
                                            Para (Email Proveedor):
                                            {mailToManual && (
                                                <span className="badge bg-warning text-dark ms-2 small">
                                                    <i className="bi bi-pencil-fill me-1"></i>No encontrado en SAP — ingresá manualmente
                                                </span>
                                            )}
                                        </label>
                                        <input
                                            type="email"
                                            className={`form-control ${mailToManual ? 'border-warning border-2' : ''}`}
                                            value={mailTo}
                                            onChange={e => setMailTo(e.target.value)}
                                            placeholder="email@proveedor.com"
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Asunto:</label>
                                        <input type="text" className="form-control" value={mailSubject}
                                            onChange={e => setMailSubject(e.target.value)} />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Mensaje:</label>
                                        <textarea className="form-control font-monospace" rows="12" value={mailBody}
                                            onChange={e => setMailBody(e.target.value)}></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer border-top-0">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowPreview(false)}>Cancelar</button>
                                    <button type="button" className="btn btn-success px-4 fw-bold" onClick={confirmarEnvio}>
                                        <i className="bi bi-send-fill me-2"></i>Continuar con envío
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ── MODAL 2: CREDENCIALES DEL REMITENTE ───────────────── */}
            {showCredModal && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
                    <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1061 }}>
                        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
                            <div className="modal-content shadow-lg border-0">
                                <div className="modal-header bg-dark text-white">
                                    <h5 className="modal-title">
                                        <i className="bi bi-shield-lock-fill me-2"></i>Autenticación de Correo
                                    </h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowCredModal(false)} disabled={sending}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="alert alert-secondary small">
                                        <i className="bi bi-envelope-paper me-1"></i>
                                        El correo sale por la casilla de <strong>Compras</strong> configurada en el
                                        Centro de Notificaciones, con tu dirección en copia y como destino de la
                                        respuesta. El envío queda registrado ahí con su resultado.
                                    </div>

                                    <div className="p-2 bg-light rounded small text-muted border">
                                        <i className="bi bi-envelope-at me-1"></i>
                                        <strong>Enviando a:</strong> {mailTo}
                                    </div>

                                    {credError && (
                                        <div className="alert alert-danger mt-3 py-2 small mb-0">
                                            <i className="bi bi-exclamation-triangle-fill me-2"></i>{credError}
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowCredModal(false)} disabled={sending}>
                                        Cancelar
                                    </button>
                                    <button type="button" className="btn btn-success px-4 fw-bold"
                                        onClick={handleEnviarConCredenciales} disabled={sending}>
                                        {sending
                                            ? <><span className="spinner-border spinner-border-sm me-2"></span>Enviando...</>
                                            : <><i className="bi bi-send-fill me-2"></i>Enviar Correo</>
                                        }
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

        </div>
    );
};

export default AsistenteReclamos;